import {
    compareActivityJobsNewestFirst,
    isActiveActivityJobStatus,
    isCompletedActivityJobStatus,
    isFailedActivityJobStatus,
    isTerminalActivityJobStatus,
    representativeActivityJob,
} from './job';
import type {
    ActivityIntent,
    ActivityItem,
    ActivityJob,
    ActivitySection,
    ActivitySections,
} from './models';

export function isActivityTerminal(item: ActivityItem): boolean {
    return (
        item.jobs.length > 0 &&
        item.jobs.every((job) => isTerminalActivityJobStatus(job.status))
    );
}

export function canDismissActivity(item: ActivityItem): boolean {
    return item.availability === 'unavailable' || isActivityTerminal(item);
}

export function activityIntentFor(item: ActivityItem): ActivityIntent | null {
    if (item.availability === 'unavailable' || !isActivityTerminal(item)) {
        return null;
    }

    const terminalJobs = [...item.jobs].sort(compareActivityJobsNewestFirst);

    for (const job of terminalJobs) {
        const intent = receiptIntentFor(job, item.correlationId);
        if (intent) return intent;
    }

    if (
        hasFailure(item) ||
        !isCompletedActivityJobStatus(
            representativeActivityJob(item.jobs).status
        )
    ) {
        return null;
    }

    for (const job of terminalJobs) {
        if (!isCompletedActivityJobStatus(job.status)) continue;
        const intent = completedJobIntentFor(job, item.availability);
        if (intent) return intent;
    }

    return null;
}

function receiptIntentFor(
    job: ActivityJob,
    correlationId: ActivityItem['correlationId']
): ActivityIntent | null {
    if (
        !isReceiptJobType(job.jobType) ||
        (!isCompletedActivityJobStatus(job.status) && job.status !== 'Failed')
    ) {
        return null;
    }
    const buildingPublicId = job.buildingPublicId?.trim();
    const batchId = correlationId?.trim() || job.jobId.trim();
    if (!buildingPublicId || !batchId) return null;
    return { type: 'view-receipt', buildingPublicId, batchId };
}

function completedJobIntentFor(
    job: ActivityJob,
    availability: ActivityItem['availability']
): ActivityIntent | null {
    if (job.stage === 'export' && job.buildingPublicId && job.jobId) {
        return exportIntentFor(job, job.buildingPublicId, availability);
    }
    if (
        job.stage === 'preview' &&
        job.buildingPublicId &&
        job.documentPublicId
    ) {
        return {
            type: 'open-preview',
            buildingPublicId: job.buildingPublicId,
            documentPublicId: job.documentPublicId,
        };
    }
    return null;
}

function exportIntentFor(
    job: ActivityJob,
    buildingPublicId: string,
    availability: ActivityItem['availability']
): ActivityIntent | null {
    const { jobId } = job;
    switch (job.exportKind) {
        case 'selected-documents':
            if (
                availability !== 'available' ||
                job.downloadAvailable !== true ||
                (job.expiresAt && Date.parse(job.expiresAt) <= Date.now())
            ) {
                return null;
            }
            return {
                type: 'download-selected-export',
                buildingPublicId,
                jobId,
            };
        case 'pack':
            if (!job.packId) return null;
            return {
                type: 'download-pack-export',
                buildingPublicId,
                jobId,
                packId: job.packId,
            };
        case 'documents':
            if (job.packId) return null;
            return { type: 'download-export', buildingPublicId, jobId };
        default:
            return null;
    }
}

export function activitySectionFor(item: ActivityItem): ActivitySection {
    if (
        item.availability === 'unavailable' ||
        hasFailure(item) ||
        hasActivityWarning(item)
    ) {
        return 'needs-attention';
    }

    if (!isActivityTerminal(item)) {
        return 'in-progress';
    }

    if (!item.actedOn && activityIntentFor(item)) {
        return 'ready';
    }

    return 'earlier';
}

export function deriveActivitySections(
    items: readonly ActivityItem[]
): ActivitySections {
    const sections: {
        inProgress: ActivityItem[];
        needsAttention: ActivityItem[];
        ready: ActivityItem[];
        earlier: ActivityItem[];
    } = {
        inProgress: [],
        needsAttention: [],
        ready: [],
        earlier: [],
    };

    for (const item of [...items].sort(compareItemsNewestFirst)) {
        switch (activitySectionFor(item)) {
            case 'in-progress':
                sections.inProgress.push(item);
                break;
            case 'needs-attention':
                sections.needsAttention.push(item);
                break;
            case 'ready':
                sections.ready.push(item);
                break;
            case 'earlier':
                sections.earlier.push(item);
                break;
        }
    }

    return sections;
}

export function deriveAttentionCount(items: readonly ActivityItem[]): number {
    return items.filter(
        (item) =>
            !item.seen && isAttentionActivitySection(activitySectionFor(item))
    ).length;
}

export function hasInProgressActivity(items: readonly ActivityItem[]): boolean {
    return items.some(
        (item) =>
            item.availability === 'available' &&
            item.jobs.some((job) => isActiveActivityJobStatus(job.status))
    );
}

export function isAttentionActivitySection(section: ActivitySection): boolean {
    return section === 'needs-attention' || section === 'ready';
}

function hasFailure(item: ActivityItem): boolean {
    return item.jobs.some((job) => isFailedActivityJobStatus(job.status));
}

export function hasActivityWarning(item: ActivityItem): boolean {
    return item.jobs.some((job) => job.warning);
}

function isReceiptJobType(jobType: string): boolean {
    return (
        jobType === 'DocumentImport' ||
        jobType === 'SharePointImport' ||
        jobType === 'DocumentClassification' ||
        jobType === 'DocumentIntelligence'
    );
}

function compareItemsNewestFirst(
    left: ActivityItem,
    right: ActivityItem
): number {
    return Date.parse(right.updatedAtUtc) - Date.parse(left.updatedAtUtc);
}
